import React from "react";
import { getBehaviourColourForPosition } from "@signolteam/shared/colours";
import AppUtils from "@src/utils/appUtils";
import { BehaviourGraphWrapper } from "@src/components/graphs";
import { CardContainer } from "@src/components/cards";
import { useDCContext } from "@src/context/dcContext";

const renderMultilineContent = (content) => {
  const rawContent = content.props?.children || content;
  if (typeof rawContent !== "string") {
    return <>{rawContent}</>;
  }
  const sanitized = AppUtils.removeUnicodeCharacters(rawContent);
  const items = sanitized
    .split(/\\n|•/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

  return (
    <ul className={items.length > 1 ? "list-disc pl-6" : "list-none"}>
      {items.map((item, idx) => (
        <li key={idx}>{item}</li>
      ))}
    </ul>
  );
};

const DefinitionCard = (props) => {
  const {
    title,
    content,
    color,
    behaviourAcronym,
    displayAcronym: displayAcronymProp,
    customerCode,
    currentVehicle,
  } = props;
  const displayAcronym = displayAcronymProp || behaviourAcronym;
  const dcContext = useDCContext();
  const {
    getDynamicReplacement: dc,
    existsDynamicReplacements: dcExists,
    getAllDynamicReplacements: dcAll,
  } = dcContext;
  const extraContentKey =
    customerCode && behaviourAcronym
      ? `captain__behaviours__extra_description_content__${customerCode.toLowerCase()}__${behaviourAcronym.toLowerCase()}__extra_content`
      : "";
  const hasExtraContent = dcExists ? dcExists(extraContentKey) : "";
  const extraContent = hasExtraContent
    ? dc(extraContentKey, { BEHAVIOUR_ACRONYM: displayAcronym })
    : "";

  const specificVehicleName = AppUtils.capitalizeFirstLetter(
    currentVehicle?.name,
  );
  const specificValuesTitle = dc(
    "captain__behaviours__extra_description_content__specific_thresholds_title",
    { VEHICLE_NAME: specificVehicleName, BEHAVIOUR_ACRONYM: displayAcronym },
    `* ${specificVehicleName} specific thresholds:`,
  );
  const specificVehicleValues =
    currentVehicle?.settings &&
    currentVehicle?.settings[
      `${behaviourAcronym.toLowerCase()}__definition_values`
    ];

  return (
    <div className={`p-4 mb-4 bg-${color}-100 rounded-xl`}>
      <div className={`text-${color}-500 font-bold pb-1`}>{title}</div>
      <div className="flex flex-col">{renderMultilineContent(content)}</div>
      {hasExtraContent && <div className="pt-4">{extraContent}</div>}
      {currentVehicle?.settings && specificVehicleValues && (
        <>
          <div className="font-bold pt-4">{specificValuesTitle}</div>
          <div>{renderMultilineContent(specificVehicleValues)}</div>
        </>
      )}
    </div>
  );
};

const sanitiseBr = (terms) => {
  return terms.replace(/<p><br><\/p>/g, "");
};

//TODO - Create story and clean up container
const BehaviourDescriptionSection = ({
  behaviours,
  customer,
  target,
  exclusions,
  currentVehicle,
}) => {
  const targetField = target?.behaviourAcronym || target?.behaviourType;
  const dcContext = useDCContext();
  const { getDynamicReplacement: dc, existsDynamicReplacements: dcExists } =
    dcContext;
  const bvr = behaviours?.find((b) => b.behaviourAcronym === targetField);
  const color = getBehaviourColourForPosition(
    !!target?.order && target.order > 0 ? target.order : bvr.order,
  ).name;

  const acronym = targetField
    ? targetField
    : bvr?.behaviourAcronym
      ? bvr.behaviourAcronym
      : bvr?.acronym;

  const displayAcronym = bvr?.behaviourGroupCode || acronym;

  let tableContent = "";
  if (exclusions) {
    const matchedKey = Object.keys(exclusions).find(
      (bvrName) =>
        bvrName.replace(/_/g, " ").toLowerCase() ===
        bvr.behaviourName?.toLowerCase(),
    );
    if (matchedKey) {
      exclusions[matchedKey].forEach((dimension) => {
        tableContent += `• ${displayAcronym} ${dimension.field_name} exclusions: ${dimension.values.join(", ")}\n`;
      });
    }
  }
  const reasons = bvr?.reasons?.replaceAll("\n", "</br>") || "";

  const acronymAOrAn = AppUtils.startsWithAVowel(displayAcronym) ? "an" : "a";

  const customerCode = customer?.customerCode;

  return (
    <div className="py-4">
      <section>
        <div className="h-full">
          <div className="w-full h-full mx-auto px-4">
            {currentVehicle?.settings && (
              <div className="p-1 my-2">
                <h2 className="text-xl font-bold mb-1">
                  {displayAcronym} on the {currentVehicle?.name}
                </h2>
                <p className="p-1">
                  To keep {displayAcronym} fair and relevant, the definition for
                  success is specific to the vessel you are on. If the vessel
                  stated is incorrect, please contact us.
                </p>
              </div>
            )}
            <div className="flex flex-col md:flex-row">
              <div className="md:w-1/2">
                <DefinitionCard
                  title={dc(
                    "captain__targets__bvr_details__definition__title",
                    { BEHAVIOUR_ACRONYM: displayAcronym },
                  )}
                  content={dc(
                    "captain__targets__bvr_details__definition__content",
                    {
                      BEHAVIOUR_ACRONYM: displayAcronym,
                      BEHAVIOUR_DESCRIPTION: bvr?.description,
                    },
                  )}
                  color={color}
                  behaviourAcronym={acronym}
                  displayAcronym={displayAcronym}
                  customerCode={customerCode}
                  currentVehicle={currentVehicle}
                />
                {bvr?.fuelSavingsSource && (
                  <DefinitionCard
                    title={dc(
                      "captain__targets__bvr_details__fuel_saved__title",
                      { BEHAVIOUR_ACRONYM: displayAcronym },
                    )}
                    content={dc(
                      "captain__targets__bvr_details__fuel_saved__content",
                      {
                        FUEL_SAVINGS: bvr?.fuelSavingsSource,
                        BEHAVIOUR_ACRONYM: displayAcronym,
                      },
                      "",
                    )}
                    color={color}
                  />
                )}
              </div>
              <div className="md:pl-4 md:w-1/2">
                {bvr?.reasons && (
                  <DefinitionCard
                    title={dc(
                      "captain__targets__bvr_details__exception__title",
                      { BEHAVIOUR_ACRONYM: displayAcronym },
                    )}
                    content={dc(
                      "captain__targets__bvr_details__exception__content",
                      {
                        REASONS: reasons,
                        EXCLUSIONS: tableContent,
                        BEHAVIOUR_ACRONYM: displayAcronym,
                      },
                      "",
                    )}
                    color={color}
                  />
                )}
                {dcExists(
                  `captain__targets__bvr_details__terminology__${acronym.toLowerCase()}`,
                ) &&
                  !!bvr?.terminology && (
                    <DefinitionCard
                      title={dc(
                        "captain__targets__bvr_details__terminology__title",
                        {
                          BEHAVIOUR_ACRONYM: displayAcronym,
                          BEHAVIOUR_NAME: bvr?.behaviourName || displayAcronym,
                        },
                      )}
                      content={dc(
                        `captain__targets__bvr_details__terminology__content`,
                        {
                          TERMS: sanitiseBr(bvr?.terminology),
                          BEHAVIOUR_ACRONYM: displayAcronym,
                        },
                        "",
                      )}
                      color={color}
                    />
                  )}
              </div>
            </div>
            <CardContainer padding={4} shadow={false}>
              <div
                className={`w-full flex justify-center text-${color}-500 font-bold`}
              >{`An example of ${acronymAOrAn} ${displayAcronym} success`}</div>
              <BehaviourGraphWrapper
                bvr={{
                  ...bvr,
                  behaviourAcronym: `${bvr.behaviourGroupCode || bvr.behaviourAcronym}:${bvr.behaviourAcronym}`,
                }}
                dc={dc}
                dcExists={dcExists}
                industry={customer?.industry}
                isExample={true}
                customerCode={customerCode}
                featureCheck={AppUtils.isFeatureAvailableForCustomer}
              />
            </CardContainer>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BehaviourDescriptionSection;
